import { Component, OnInit } from '@angular/core';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { BaseComponent } from '../../../base/base.component';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-case-management',
  imports: [
    PageHeaderComponent,
    TableComponent,
    ColumnComponent,
    TagComponent,
    EmptyStateComponent,
    ProgressbarComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-page-header label="Case Management" />
    @if (loading) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-briefcase-medical"
        label="Loading Case Management"
        message="Retrieving case management data..."
      >
        <div class="tw-py-4">
          <app-progressbar />
        </div>
      </app-empty-state>
    } @else if (error) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-triangle-exclamation"
        label="An error has occurred"
        message="Unable to load case management data."
      />
    } @else if (dataSource.length === 0) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-briefcase-medical"
        label="No Case Management"
        message="There are no case management records available for this claim."
      />
    } @else {
      <app-table
        [dataSource]="dataSource"
        [pageSize]="10"
        [scrollable]="false"
        recordLabel="Case"
        [stripedRows]="false"
        [error]="error"
      >
        <app-column label="Service" width="15%" name="service">
          <ng-template let-data>
            <app-link-label
              (click)="
                view.navigateCurrentClaim([
                  'case-management-detail',
                  data.serviceId,
                ])
              "
              >{{ data.service }}</app-link-label
            >
          </ng-template>
        </app-column>
        <app-column name="treatmentPlan" label="Treatment Plan" width="15%">
          <ng-template let-data>
            <app-tag [type]="getTagStyle(data.treatmentPlan)">{{
              getBooleanValue(data.treatmentPlan)
            }}</app-tag>
          </ng-template>
        </app-column>
        <app-column
          name="physicianOrPeerReview"
          label="Physician/Peer Review"
          width="15%"
        >
          <ng-template let-data>
            <app-tag [type]="getTagStyle(data.physicianOrPeerReview)">{{
              getBooleanValue(data.physicianOrPeerReview)
            }}</app-tag>
          </ng-template>
        </app-column>

        <app-column
          name="treatmentStatus"
          label="Treatment Status"
          width="15%"
        />
        <app-column
          name="treatmentReceived"
          label="Treatment Received"
          format="d"
          width="15%"
        />
        <app-column name="reviewStatus" label="Review Status" width="10%" />
        <app-column
          name="reviewReceived"
          label="Review Received"
          format="d"
          width="15%"
        />
      </app-table>
    }
  `,
  styles: [],
})
export class CaseManagementComponent extends BaseComponent implements OnInit {
  dataSource: any[] = [];
  loading = true;
  error: any = null;

  getTagStyle(status: string): TagType {
    return status ? 'Purple' : 'Grey';
  }

  getBooleanValue(value: boolean): string {
    return value ? 'Yes' : 'No';
  }

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    this.view.hideLoading();
    const claimKey = getClaimKey();
    const response = await this.api.caseManagement.get(claimKey);
    this.dataSource = response.caseManagements || [];
  }
}
